import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface SuggestInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Every spelling this field already has in the library. */
  options: string[];
  className?: string;
  id?: string;
  placeholder?: string;
  ariaLabel?: string;
  autoFocus?: boolean;
  /** Shown in front of each suggestion, not stored (the @ on a handle). */
  prefix?: string;
}

const MIN_CHARS = 2;
const MAX_SHOWN = 8;
const MAX_HEIGHT = 200;

/** Matches for what's typed: what starts with it first, then what contains it.
 *  An exact hit is left out — there's nothing to pick. */
function matchesFor(options: string[], query: string): string[] {
  const q = query.trim().toLowerCase();
  if (q.length < MIN_CHARS) return [];
  const starts: string[] = [];
  const contains: string[] = [];
  for (const o of options) {
    const lower = o.toLowerCase();
    if (lower === q) continue;
    if (lower.startsWith(q)) starts.push(o);
    else if (lower.includes(q)) contains.push(o);
  }
  return [...starts, ...contains].slice(0, MAX_SHOWN);
}

/** A text field that offers what the library already says, and never changes
 *  what was typed on its own — the list is picked from, never auto-applied.
 *  Silently completing a name is how "Arm Drag" becomes "Arm Drag to Back"
 *  without the submitter noticing.
 *
 *  The list is portalled to the body rather than drawn in place: the info modal
 *  clips its own overflow, which would cut the lower fields' matches in half. */
export default function SuggestInput({ value, onChange, options, className, id, placeholder, ariaLabel, autoFocus, prefix }: SuggestInputProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState<React.CSSProperties | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const matches = open ? matchesFor(options, value) : [];
  const shown = matches.length > 0;

  // Anchored to the field itself, so the page scrolling under it doesn't leave
  // the list floating somewhere else.
  const place = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - 8;
    const above = r.top - 8;
    // Flip up only when there genuinely isn't room under the field.
    const flip = below < 120 && above > below;
    setPos({
      left: r.left,
      width: r.width,
      maxHeight: Math.min(MAX_HEIGHT, flip ? above : below),
      ...(flip ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
    });
  }, []);

  useLayoutEffect(() => {
    if (shown) place();
  }, [shown, matches.length, place]);

  useEffect(() => {
    if (!shown) return;
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [shown, place]);

  const pick = (option: string) => {
    onChange(option);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!shown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i <= 0 ? matches.length - 1 : i - 1));
    } else if (e.key === 'Enter' && active >= 0) {
      // Only swallows the key when a suggestion is actually highlighted, so
      // Enter still submits the form the rest of the time.
      e.preventDefault();
      pick(matches[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div className="suggest">
      <input
        ref={inputRef}
        id={id}
        className={className}
        type="text"
        autoComplete="off"
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoFocus={autoFocus}
        role="combobox"
        aria-expanded={shown}
        aria-controls={shown ? listId : undefined}
        aria-autocomplete="list"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActive(-1); }}
        onFocus={() => setOpen(true)}
        onBlur={() => { setOpen(false); setActive(-1); }}
        onKeyDown={onKeyDown}
      />
      {shown && pos && createPortal(
        <ul className="suggest-list" id={listId} role="listbox" style={pos}>
          {matches.map((m, i) => (
            <li
              key={m}
              role="option"
              aria-selected={i === active}
              className={`suggest-option${i === active ? ' suggest-option--active' : ''}`}
              // Blur would fire before click and close the list out from under
              // the pointer — take the press without letting the field lose focus.
              onMouseDown={(e) => { e.preventDefault(); pick(m); }}
              onMouseEnter={() => setActive(i)}
            >
              {prefix}{m}
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </div>
  );
}
